import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOJsonLD from '@/components/base/SEOJsonLD';
import { categories, getVideosByCategory } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dianshi');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const videos = getVideosByCategory(activeSlug);

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${currentCategory.name} - 红桃tv高清视频`,
    "description": currentCategory.description,
    "url": `https://hongtaotv.com/category/${currentCategory.slug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "红桃tv",
      "url": "https://hongtaotv.com/"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://hongtaotv.com/" },
      { "@type": "ListItem", "position": 2, "name": "内容分类", "item": "https://hongtaotv.com/category/dianshi" },
      { "@type": "ListItem", "position": 3, "name": currentCategory.name }
    ]
  };

  return (
    <>
      <SEOJsonLD data={categorySchema} />
      <SEOJsonLD data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main>
        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500">
              {currentCategory.description} — 共{videos.length}部作品
            </p>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {videos.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {videos.map((video) => (
                <Link
                  key={video.id}
                  to={`/category/${video.category}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.cover}
                      alt={video.title}
                      title={`${video.title} - 红桃tv ${currentCategory.name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      loading="lazy"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded">
                      {video.duration}
                    </div>
                    {video.rating >= 9.0 && (
                      <div className="absolute top-2 left-2">
                        <span className="bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded">热门</span>
                      </div>
                    )}
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="font-medium text-sm md:text-base text-foreground-900 mb-1.5 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                      {video.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        {video.rating}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {video.views}
                      </span>
                      <div className="ml-auto flex items-center gap-1 flex-wrap">
                        {video.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-xs bg-background-100 px-1.5 py-0.5 rounded text-foreground-500">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                <i className="ri-video-line text-foreground-400 text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}